import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, Map, FolderOpen, CalendarCheck, LogOut,
  Menu, X, Mountain, ChevronRight, Globe, Settings, BookOpen, Mail, MessageCircle, Sparkles,
} from 'lucide-react';
import SEO from '../SEO';

const navItems = [
  { label: 'Dashboard', href: '/admin', icon: LayoutDashboard },
  { label: 'Turlar', href: '/admin/turlar', icon: Map },
  { label: 'Etkinlikler', href: '/admin/etkinlikler', icon: Sparkles },
  { label: 'Kategoriler', href: '/admin/kategoriler', icon: FolderOpen },
  { label: 'Rezervasyonlar', href: '/admin/rezervasyonlar', icon: CalendarCheck },
  { label: 'Mesajlar', href: '/admin/mesajlar', icon: Mail },
  { label: 'Blog', href: '/admin/blog', icon: BookOpen },
  { label: 'Yorumlar', href: '/admin/yorumlar', icon: MessageCircle },
  { label: 'Ayarlar', href: '/admin/ayarlar', icon: Settings },
];

const AdminLayout = ({ children }) => {
  const { logout, username } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-[#f5f5f0] flex">
      <SEO title="Admin" noIndex />
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#1B1B1B] transform transition-transform lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="px-6 py-5 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#C8D843] rounded-lg flex items-center justify-center">
                <Mountain size={20} className="text-[#1B1B1B]" />
              </div>
              <div>
                <p className="text-white font-bold text-sm" style={{ fontFamily: 'DM Sans, sans-serif' }}>camption</p>
                <p className="text-white/40 text-xs">Yönetim Paneli</p>
              </div>
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-3 py-4 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href || (item.href !== '/admin' && location.pathname.startsWith(item.href));
              const Icon = item.icon;
              return (
                <Link
                  key={item.href} to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? 'bg-[#C8D843]/15 text-[#C8D843]' : 'text-white/60 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <Icon size={18} />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Visit Site */}
          <div className="px-3 pb-2">
            <Link
              to="/" target="_blank"
              className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-white/40 hover:text-white/70 hover:bg-white/5 transition-colors"
            >
              <Globe size={18} />
              Siteyi Görüntüle
              <ChevronRight size={14} className="ml-auto" />
            </Link>
          </div>

          {/* User / Logout */}
          <div className="px-3 pb-5 border-t border-white/10 pt-4">
            <div className="flex items-center justify-between px-4">
              <span className="text-sm text-white/60">{username || 'Admin'}</span>
              <button onClick={handleLogout} className="p-1.5 text-white/40 hover:text-red-400 transition-colors" title="Çıkış">
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 flex items-center gap-4 sticky top-0 z-20">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden p-1.5 hover:bg-gray-100 rounded-lg">
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <h2 className="text-lg font-bold text-[#1B1B1B]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {navItems.find(n => location.pathname === n.href || (n.href !== '/admin' && location.pathname.startsWith(n.href)))?.label || 'Admin'}
          </h2>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 sm:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
